import {
  enumInvoiceSortOption,
  InvoiceSortOption,
  InvoiceStatus,
  InvoiceType,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { billAccountsQueryAtom } from '../_graphql/queryBillAccounts';
import { invoicesQueryAtom } from '../_graphql/queryFinanceInvoices';

import { InvoiceRecord } from '~/modules/finance/types';
import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $columnSelectedStatuses =
  atomFromQueryString.stringSet<InvoiceStatus>('colStatuses');
export const $columnSelectedTypes =
  atomFromQueryString.stringSet<InvoiceType>('types');
export const $columnSelectedAccountIds = atomFromQueryString.stringSet('acc');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $viewMode = atomFromQueryString.string<'flat' | 'grouped'>(
  'view',
  'flat',
);

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isPaymentDrawerOpen = atom<boolean>(false);
export const $isInvoiceWizardOpen = atom<boolean>(false);
export const $invoicesToReceive = atom<InvoiceRecord[]>([]);
export const $currentSorting = atom<InvoiceSortOption[]>([
  enumInvoiceSortOption.DocumentDateDesc,
]);

// 🔘🔘🔘 FILTER ATOMS
export const $accountFilterSearchTerm = atom<string | undefined>(undefined);

// 🟢🟢🟢 FILTER REMOTE DATA
export const $remoteFilterAccounts = billAccountsQueryAtom((get) => {
  const searchTerm = get($accountFilterSearchTerm);
  return {
    variables: { searchTerm, limit: 20 },
  };
});

// 🟢🟢🟢 REMOTE DATA
export const $remoteInvoices = invoicesQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const columnSelectedStatuses = get($columnSelectedStatuses);
  const columnSelectedTypes = get($columnSelectedTypes);
  const columnSelectedAccountIds = get($columnSelectedAccountIds);
  const currentSorting = get($currentSorting);
  const viewMode = get($viewMode);
  const limit = get($limit);
  const offset = get($offset);

  const sorting: InvoiceSortOption[] =
    viewMode === 'grouped'
      ? [enumInvoiceSortOption.AccountAsc, ...currentSorting]
      : currentSorting;

  const params: {
    searchTerm?: string;
    statuses?: InvoiceStatus[];
    types?: InvoiceType[];
    accountIDs?: string[];
    limit?: number;
    offset?: number;
    sorting: InvoiceSortOption[];
  } = {
    limit,
    offset,
    sorting,
  };

  if (searchTerm) params.searchTerm = searchTerm;
  if (columnSelectedStatuses.size > 0)
    params.statuses = Array.from(columnSelectedStatuses);
  if (columnSelectedTypes.size > 0)
    params.types = Array.from(columnSelectedTypes);
  if (columnSelectedAccountIds.size > 0)
    params.accountIDs = Array.from(columnSelectedAccountIds);

  return {
    variables: params,
  };
});
